import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom, of } from 'rxjs';

import { FileManagerUploadedFilesComponent } from '@features/file-manager-uploaded-files/file-manager-uploaded-files.component';
import { SiteSelectDropdownComponent } from '@features/site-select-dropdown/site-select-dropdown.component';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { FileMetadata } from '@shared/services/api.service';
import { DocumentLibraryService } from '@shared/services/document-library.service';
import { DocumentType, EntityType, EventType } from '@shared/services/gql.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { EventService } from '@shared/store/event/event.service';
import { Option } from '@shared/types/components.type';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    FileManagerComponent,
    IconComponent,
    AsyncPipe,
    FileManagerUploadedFilesComponent,
    SiteSelectDropdownComponent,
    ModalComponent,
  ],
  selector: 'aux-document-upload',
  styles: [
    `
      :host ::ng-deep .site-dropdown-label {
        @apply mb-2 text-sm;
      }
    `,
  ],
  templateUrl: './document-upload.component.html',
})
export class DocumentUploadComponent implements OnInit {
  private readonly authQuery = inject(AuthQuery);
  private readonly documentLibraryService = inject(DocumentLibraryService);
  private readonly eventService = inject(EventService);
  private readonly formBuilder = inject(UntypedFormBuilder);

  loading = signal(false);

  uploadedFiles$ = of<File[]>([]);

  documentForm = this.formBuilder.group({
    documentType: undefined,
    site: undefined,
    vendor: undefined,
  });
  documentTypeList: Option<DocumentType>[] = this.documentLibraryService.getDocumentOptions();
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  metadata: FileMetadata = {};
  ref = inject<CustomOverlayRef<unknown, { sites: Option[]; vendors: Option[] }>>(CustomOverlayRef);
  vendorsList: Option[] = [];

  ngOnInit() {
    this.vendorsList = this.ref.data?.vendors || [];

    Promise.resolve()
      .then(() => {
        if (this.fileManager) {
          this.uploadedFiles$ = this.fileManager.fileQuery.selectAll();
        }
      })
      .catch(console.error);
  }

  async onUpload() {
    if (this.fileManager) {
      this.loading.set(true);
      const files = this.fileManager.fileQuery.getAll();

      this.updateFileStoreBeforeUpload(files);
      const successFM = await this.fileManager.fileService.uploadFiles({
        filterLocalFilesByUploadedFlag: true,
        insertDocument: false,
        metadata: this.metadata,
        overrideFilePath: undefined,
        removeFilesOnPartialFailure: true,
        showSuccess: false,
      });

      if (successFM) {
        const successDL = this.documentLibraryService.uploadDocuments(
          files,
          this.documentForm.value
        );

        const idList = [] as string[];
        const successUpload = await successDL;

        successUpload.forEach((file) => {
          if (file instanceof Error) {
            return;
          }

          if (file.success && file.data?.id) {
            idList.push(file.data?.id);
          }
        });

        if (!this.authQuery.isAuxAdmin() && idList.length > 0) {
          await firstValueFrom(
            this.eventService.processEvent$({
              entity_id: '',
              entity_type: EntityType.DOCUMENT,
              payload: JSON.stringify({
                file_name: files[0].fileName,
                idList,
                uploaded_by: `${this.authQuery.getFullName()} ${this.authQuery.getEmail()}`,
              }),
              type: EventType.DOCUMENT_UPLOADED_NOTIFICATION,
            })
          );
        }

        this.ref.close(true);
      }

      this.loading.set(false);
    }
  }

  pathFn: () => string = () => '';

  private updateFileStoreBeforeUpload(files: File[]) {
    const { documentType, site, vendor } = this.documentForm.value;

    files.forEach((file) => {
      this.fileManager?.fileStore.update(file.id, {
        ...file,
        bucket_key: `${this.documentLibraryService.buildDocumentS3Path(file.bucket_key, documentType, site, vendor)}`,
      });
    });
  }
}
